/**
 * Gesprächsverhalten des Gesprächspartners im Telefonvermerk
 * (Kundenfeedback 09.10.2026): „Bei den Telefonvermerken soll man auch
 * vermerken können, ob der Gegner freundlich, unfreundlich, aufbrausend,
 * verständnislos, etc. gewesen ist.“
 *
 * Mehrfachauswahl aus einer festen Liste plus optionale Ergänzung. Die
 * Schlüssel und ihre Reihenfolge sind mit dem Backend abgestimmt
 * (backend/internal/fall/phonecall/contactbehavior.go).
 */
import { gtKey } from "@/i18n/gt";

export type ContactBehaviorKey =
  | "freundlich"
  | "sachlich"
  | "kooperativ"
  | "unfreundlich"
  | "aufbrausend"
  | "verstaendnislos"
  | "ausweichend"
  | "drohend";

/** positiv (grün), neutral (grau), negativ (rot/orange). */
export type ContactBehaviorTone = "positive" | "neutral" | "negative";

export interface ContactBehaviorOption {
  key: ContactBehaviorKey;
  /** Deutscher Anzeigename; Übersetzung über $gt(label). */
  label: string;
  tone: ContactBehaviorTone;
  /** mdi-Icon für den Chip. */
  icon: string;
}

/** Maximale Länge der Ergänzung (VARCHAR(255) im Backend). */
export const CONTACT_BEHAVIOR_NOTE_MAX = 255;

const ICON_BY_TONE: Record<ContactBehaviorTone, string> = {
  positive: "mdi-emoticon-happy-outline",
  neutral: "mdi-emoticon-neutral-outline",
  negative: "mdi-emoticon-angry-outline",
};

/** Vuetify-Farbe je Tonlage. */
const COLOR_BY_TONE: Record<ContactBehaviorTone, string> = {
  positive: "success",
  neutral: "grey-darken-1",
  negative: "error",
};

function option(key: ContactBehaviorKey, label: string, tone: ContactBehaviorTone, icon = ICON_BY_TONE[tone]): ContactBehaviorOption {
  return { key, label, tone, icon };
}

/** Kanonische Reihenfolge: positiv, sachlich, dann negativ. */
export const CONTACT_BEHAVIOR_OPTIONS: readonly ContactBehaviorOption[] = Object.freeze([
  option("freundlich", gtKey("Freundlich"), "positive"),
  option("sachlich", gtKey("Sachlich"), "neutral"),
  option("kooperativ", gtKey("Kooperativ"), "positive", "mdi-handshake-outline"),
  option("unfreundlich", gtKey("Unfreundlich"), "negative", "mdi-emoticon-sad-outline"),
  option("aufbrausend", gtKey("Aufbrausend"), "negative"),
  option("verstaendnislos", gtKey("Verständnislos"), "negative", "mdi-emoticon-confused-outline"),
  option("ausweichend", gtKey("Ausweichend"), "neutral", "mdi-emoticon-neutral-outline"),
  option("drohend", gtKey("Drohend / einschüchternd"), "negative", "mdi-alert-octagon-outline"),
]);

const BY_KEY = new Map<string, ContactBehaviorOption>(CONTACT_BEHAVIOR_OPTIONS.map((o) => [o.key, o]));

export function contactBehaviorOption(key: string): ContactBehaviorOption | undefined {
  return BY_KEY.get(key);
}

/**
 * Bekannte Schlüssel, ohne Doppelte, in kanonischer Reihenfolge – so wie das
 * Backend sie speichert. Unbekannte Einträge entfallen.
 */
export function normalizeContactBehavior(keys: readonly string[] | null | undefined): ContactBehaviorKey[] {
  const selected = new Set((keys ?? []).map((k) => String(k).trim().toLowerCase()));
  return CONTACT_BEHAVIOR_OPTIONS.filter((o) => selected.has(o.key)).map((o) => o.key);
}

/** Optionen zu den Schlüsseln (für die Chips in den Listen). */
export function contactBehaviorOptions(keys: readonly string[] | null | undefined): ContactBehaviorOption[] {
  return normalizeContactBehavior(keys).map((k) => BY_KEY.get(k) as ContactBehaviorOption);
}

export function contactBehaviorColor(tone: ContactBehaviorTone): string {
  return COLOR_BY_TONE[tone];
}

/** Ergänzung bereinigt; leer → null. */
export function normalizeContactBehaviorNote(note: string | null | undefined): string | null {
  const trimmed = (note ?? "").trim();
  return trimmed === "" ? null : trimmed;
}

/** Ist überhaupt ein Gesprächsverhalten erfasst? */
export function hasContactBehavior(call: { contactBehavior?: readonly string[] | null; contactBehaviorNote?: string | null } | null | undefined): boolean {
  if (!call) return false;
  return normalizeContactBehavior(call.contactBehavior).length > 0 || normalizeContactBehaviorNote(call.contactBehaviorNote) !== null;
}

/**
 * Lesbare Zusammenfassung wie in der E-Mail: „Freundlich, Kooperativ – sehr
 * geduldig“. `translate` übersetzt die Anzeigenamen (z. B. $gt).
 */
export function describeContactBehavior(
  keys: readonly string[] | null | undefined,
  note: string | null | undefined,
  translate: (s: string) => string = (s) => s,
): string {
  const labels = contactBehaviorOptions(keys).map((o) => translate(o.label)).join(", ");
  const extra = normalizeContactBehaviorNote(note);
  if (labels && extra) return `${labels} – ${extra}`;
  return labels || extra || "";
}

/** Überwiegende Tonlage der Auswahl (für Filter und Hervorhebung). */
export function contactBehaviorTone(keys: readonly string[] | null | undefined): ContactBehaviorTone | null {
  const tones = contactBehaviorOptions(keys).map((o) => o.tone);
  if (tones.length === 0) return null;
  if (tones.includes("negative")) return "negative";
  if (tones.includes("positive")) return "positive";
  return "neutral";
}

/** Filterwerte der globalen Telefonliste. */
export type ContactBehaviorFilter = "all" | "negative" | "positive" | "none" | ContactBehaviorKey;

export function matchesContactBehaviorFilter(
  keys: readonly string[] | null | undefined,
  filter: ContactBehaviorFilter,
): boolean {
  if (filter === "all") return true;
  const normalized = normalizeContactBehavior(keys);
  if (filter === "none") return normalized.length === 0;
  if (filter === "negative" || filter === "positive") {
    return normalized.some((k) => BY_KEY.get(k)?.tone === filter);
  }
  return normalized.includes(filter);
}
